import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader
} from '@mui/material';
import {
  Business,
  Science,
  Medication,
  LocalHospital,
  People,
  Schedule,
  Upload,
  Category as EntitiesIcon,
  Article as ArticleIcon,
  SpaceDashboard as DashboardIcon,
  Flag as FlagIcon,
  AccountTree as AccountTreeIcon,

  Hub as HubIcon,
  Logout as LogoutIcon,
  ArrowDropDown,
  AccountTree as MapIcon
} from '@mui/icons-material';
import { useAuth } from '../../../hooks/useAuth';
import EnvironmentToggle from '../EnvironmentToggle/EnvironmentToggle';

// Paths that belong to the consolidated "Entities" menu — used to highlight
// the top-level button when any entity page is active.
const ENTITY_PATHS = [
  '/companies',
  '/bulk-import/companies',
  '/technologies',
  '/therapeutics',
  '/diseases'
];

// Paths grouped under the "Dashboard" dropdown — used to highlight the
// top-level button when any dashboard page is active.
const DASHBOARD_PATHS = [
  '/completion-dashboard',
  '/milestone-dashboard',
  '/company-pipelines',
  '/pipeline-intelligence',

  '/document-intelligence',
];

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.is_superuser || user?.role === 'admin';
  const isEditorOrAdmin = user?.role === 'editor' || isAdmin;

  // Menu anchor states
  const [entitiesAnchor, setEntitiesAnchor] = useState<null | HTMLElement>(null);
  const [dashboardAnchor, setDashboardAnchor] = useState<null | HTMLElement>(null);

  const isEntitiesActive = ENTITY_PATHS.some((p) => location.pathname.includes(p));
  const isDashboardActive = DASHBOARD_PATHS.some((p) => location.pathname.includes(p));

  const go = (path: string) => {
    navigate(path);
    setEntitiesAnchor(null);
    setDashboardAnchor(null);
  };

  const getRoleColor = () => {
    if (user?.is_superuser || user?.role === 'admin') return 'error';
    if (user?.role === 'editor') return 'warning';
    return 'default';
  };

  const getRoleText = () => {
    if (user?.is_superuser || user?.role === 'admin') return 'Admin';
    if (user?.role === 'editor') return 'Editor';
    return 'Viewer';
  };

  return (
    <AppBar
      position="static"
      sx={{
        bgcolor: 'white',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
      }}
    >
      <Toolbar sx={{ gap: 2, px: 3, minHeight: 64 }}>
        {/* Brand */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            color: 'primary.main',
            fontWeight: 700,
            letterSpacing: '-0.025em'
          }}
        >
          SOZO Digital Twin Editor
        </Typography>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Navigation */}
        <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
          {/* Entities Dropdown — groups Companies, Technologies, Therapeutics, Diseases */}
          <Button
            startIcon={<EntitiesIcon />}
            endIcon={<ArrowDropDown />}
            onClick={(e) => setEntitiesAnchor(e.currentTarget)}
            sx={{
              backgroundColor: isEntitiesActive ? 'primary.main' : 'transparent',
              color: isEntitiesActive ? 'white' : 'text.primary',
              '&:hover': {
                backgroundColor: isEntitiesActive ? 'primary.dark' : 'action.hover',
              }
            }}
          >
            Entities
          </Button>
          <Menu
            anchorEl={entitiesAnchor}
            open={Boolean(entitiesAnchor)}
            onClose={() => setEntitiesAnchor(null)}
          >
            {/* Companies */}
            <ListSubheader sx={{ lineHeight: '2.2em', bgcolor: 'transparent' }}>
              Companies
            </ListSubheader>
            <MenuItem onClick={() => go('/companies')}>
              <ListItemIcon><Business fontSize="small" /></ListItemIcon>
              <ListItemText>Companies</ListItemText>
            </MenuItem>
            {isEditorOrAdmin && (
              <MenuItem onClick={() => go('/bulk-import/companies')}>
                <ListItemIcon><Upload fontSize="small" /></ListItemIcon>
                <ListItemText>Bulk Import</ListItemText>
              </MenuItem>
            )}

            <Divider />

            {/* Technologies */}
            <ListSubheader sx={{ lineHeight: '2.2em', bgcolor: 'transparent' }}>
              Technologies
            </ListSubheader>
            <MenuItem onClick={() => go('/technologies')}>
              <ListItemIcon><Science fontSize="small" /></ListItemIcon>
              <ListItemText>Technologies</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => go('/technologies/map')}>
              <ListItemIcon><MapIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Technology Map</ListItemText>
            </MenuItem>

            <Divider />

            {/* Therapeutics */}
            <ListSubheader sx={{ lineHeight: '2.2em', bgcolor: 'transparent' }}>
              Therapeutics
            </ListSubheader>
            <MenuItem onClick={() => go('/therapeutics')}>
              <ListItemIcon><Medication fontSize="small" /></ListItemIcon>
              <ListItemText>Therapeutics</ListItemText>
            </MenuItem>

            <Divider />

            {/* Diseases */}
            <ListSubheader sx={{ lineHeight: '2.2em', bgcolor: 'transparent' }}>
              Diseases
            </ListSubheader>
            <MenuItem onClick={() => go('/diseases')}>
              <ListItemIcon><LocalHospital fontSize="small" /></ListItemIcon>
              <ListItemText>Diseases</ListItemText>
            </MenuItem>
          </Menu>

          {/* Dashboard Dropdown — groups Completion and Milestone dashboards */}
          <Button
            startIcon={<DashboardIcon />}
            endIcon={<ArrowDropDown />}
            onClick={(e) => setDashboardAnchor(e.currentTarget)}
            sx={{
              backgroundColor: isDashboardActive ? 'primary.main' : 'transparent',
              color: isDashboardActive ? 'white' : 'text.primary',
              '&:hover': {
                backgroundColor: isDashboardActive ? 'primary.dark' : 'action.hover',
              }
            }}
          >
            Dashboard
          </Button>
          <Menu
            anchorEl={dashboardAnchor}
            open={Boolean(dashboardAnchor)}
            onClose={() => setDashboardAnchor(null)}
          >
            <MenuItem onClick={() => go('/completion-dashboard')}>
              <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Completion Dashboard</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => go('/milestone-dashboard')}>
              <ListItemIcon><FlagIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Milestone Dashboard</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => go('/company-pipelines')}>
              <ListItemIcon><AccountTreeIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Company Pipelines</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => go('/pipeline-intelligence')}>
              <ListItemIcon><AccountTreeIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Pipeline Intelligence</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => go('/document-intelligence')}>
              <ListItemIcon><HubIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Document Intelligence</ListItemText>
            </MenuItem>
          </Menu>

          {/* Content */}
          <Button
            component={NavLink}
            to="/content-completion"
            startIcon={<ArticleIcon />}
            sx={{
              color: 'text.primary',
              '&.active': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            }}
          >
            Content
          </Button>

          {isEditorOrAdmin && (
            <Button
              component={NavLink}
              to="/llm-jobs"
              startIcon={<Schedule />}
              sx={{
                color: 'text.primary',
                '&.active': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }}
            >
              LLM
            </Button>
          )}

          {/* Editor/Admin - Jobs */}
          {isEditorOrAdmin && (
            <Button
              component={NavLink}
              to="/scheduled-jobs"
              startIcon={<Schedule />}
              sx={{
                color: 'text.primary',
                '&.active': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }}
            >
              Jobs
            </Button>
          )}

          {/* Admin - Users */}
          {isAdmin && (
            <Button
              component={NavLink}
              to="/users"
              startIcon={<People />}
              sx={{
                color: 'text.primary',
                '&.active': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }}
            >
              Users
            </Button>
          )}
        </Box>

        {/* Right side - Environment Toggle & User Info */}
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EnvironmentToggle />

            <Divider orientation="vertical" flexItem />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={user.username}
                size="small"
                variant="outlined"
              />
              <Chip
                label={getRoleText()}
                color={getRoleColor()}
                size="small"
              />
              <IconButton
                onClick={logout}
                color="primary"
                aria-label="logout"
                size="small"
              >
                <LogoutIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
